import {
  emptyConfig,
  CANNOT_DISPLAY_FILE,
  NEXTFLOW_CONFIG,
} from "@via/components/tree-structure/helpers/constants.tsx";
import {
  createMultiConfig,
  createPath,
  createTreeStructure,
  update,
} from "@via/components/tree-structure/helpers/utils.tsx";
import "@via/components/tree-structure/tree-structure.css";
import { TreeStructure } from "@via/components/tree-structure/tree-structure.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import DeleteFilesModal from "@via/pages/projects/pipelines/advanced/delete-files-modal.tsx";
import {
  AdvancedFilesActions,
  LANGUAGE_MODES,
  LANGUAGE_OPTIONS,
  ROUTES,
} from "@via/utils/constants.tsx";
import { classNames, getLanguageByFileExtension } from "@via/utils/helpers.tsx";
import {
  type AdvancedFilesInterface,
  type AnyRef,
  type TreeDataScriptInterface,
} from "@via/utils/interfaces.tsx";
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type NodeApi } from "react-arborist";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
} from "react-resizable-panels";
import MonacoEditor from "@via/components/monaco-editor/monaco-editor";
import PanelResizer from "@via/components/panel-resizer/panel-resizer.tsx";
import SelectComponent from "@via/components/select/select";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import { EmptyDataIcon } from "@via/utils/custom-icons.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { useParams } from "react-router-dom";
import {
  clearCortexSelection,
  getCortexSelectionForCurrentPage,
} from "@via/utils/cortex-selection";
import { attachCortexSelectionTracking } from "@via/utils/monaco-cortex-selection";
import {
  clearCortexMonacoTarget,
  setCortexMonacoTarget,
} from "@via/utils/cortex-monaco-target";

const HIDE_LANGUAGE_MODAL = [
  "Pipeline Script Header",
  "Pipeline Script Footer",
];

export default function PipelineAdvanced(): JSX.Element {
  const { pipelineId } = useParams();
  const { pipelineDraft, scriptPipeConfig, setScriptPipeConfig } = useGlobal();

  const [language, setLanguage] = useState(LANGUAGE_MODES.GROOVY);
  const [file, setFile] = useState<AdvancedFilesInterface>();
  const [treeData, setTreeData] = useState<TreeDataScriptInterface[]>([]);
  const [initialFile, setInitialFile] =
    useState<AdvancedFilesInterface>(emptyConfig);
  const [filesToBeDeleted, setFilesToBeDeleted] = useState<NodeApi | null>(
    null
  );
  const editorRef: AnyRef = useRef(null);
  const treeRef: AnyRef = useRef(null);
  const languageRef: AnyRef = useRef(language);

  const [isPanelCollapsed, setPanelCollapsed] = useState(false);
  const panelRef = useRef<ImperativePanelHandle | null>(null);
  const { increaseChangeCount } = useChangesDetect();

  const selectionMetaRef = useRef({
    language,
    filePath: file?.filename ?? "",
  });
  const selectionDisposableRef = useRef<{ dispose?: () => void } | null>(null);
  const lastSelectedTextRef = useRef<string>("");
  const lastFilePathRef = useRef<string | null>(null);

  useEffect(() => {
    selectionMetaRef.current = {
      language,
      filePath: file?.filename ?? "",
    };
  }, [language, file?.filename]);

  useEffect(() => {
    const nextFilePath = file?.filename ?? null;
    const prevFilePath = lastFilePathRef.current;
    lastFilePathRef.current = nextFilePath;
    if (!prevFilePath) return;
    if (nextFilePath === prevFilePath) return;

    // Reset selection when switching files (Run Logs-like behavior)
    clearCortexSelection();
    lastSelectedTextRef.current = "";
  }, [file?.filename]);

  useEffect(() => {
    return () => {
      selectionDisposableRef.current?.dispose?.();
      selectionDisposableRef.current = null;
      clearCortexSelection();
      lastSelectedTextRef.current = "";
      clearCortexMonacoTarget();
    };
  }, []);

  const handleMonacoMount = useCallback(editor => {
    setCortexMonacoTarget(editor);
    attachCortexSelectionTracking({
      editor,
      selectionMetaRef,
      selectionDisposableRef,
      lastSelectedTextRef,
    });
  }, []);

  const getPipelineAdvancedContext = useCallback(() => {
    const fileContent =
      file?.text && file.text !== CANNOT_DISPLAY_FILE ? file.text : "";
    const selection = getCortexSelectionForCurrentPage();
    const selectedCode =
      selection && selection.filePath === (file?.filename ?? "")
        ? selection.text
        : "";
    const selectionLabel =
      selection && selectedCode
        ? `Selected code (${selection.filePath}):\n\`\`\`${selection.language || language}\n${selectedCode}\n\`\`\`\n`
        : "";
    return {
      pipelineId,
      pipelineName: pipelineDraft?.name ?? "",
      filePath: file?.filename ?? "",
      language,
      selectedCode,
      selectedCodeMeta:
        selection && selectedCode
          ? {
              language: selection.language,
              filePath: selection.filePath,
              updatedAt: selection.updatedAt,
            }
          : null,
      content: selectedCode ? selectionLabel : fileContent,
    };
  }, [pipelineId, pipelineDraft?.name, file?.filename, file?.text, language]);

  useRegisterCortexContext({
    routePattern: ROUTES.PIPELINE_ADVANCED,
    contextName: "Pipeline Advanced",
    getContext: getPipelineAdvancedContext,
    systemPrompt:
      "You are helping edit Pipeline Advanced scripts and configuration files. Use the provided file content and metadata to answer questions and suggest safe changes.",
    enabled: !!pipelineDraft && !!file,
  });

  const defaultLanguage = useMemo(() => {
    return (
      LANGUAGE_OPTIONS.find(option => option.value === language) ||
      LANGUAGE_OPTIONS.find(option => option.value === LANGUAGE_MODES.GROOVY)
    );
  }, [file, language]);

  useEffect(() => {
    if (pipelineDraft) {
      // Directly compute the configuration and tree structure as needed without intermediate assignments
      const editorScriptPipeConfig =
        scriptPipeConfig.length > 0
          ? scriptPipeConfig
          : createMultiConfig(pipelineDraft);
      const treeStructure = createTreeStructure(editorScriptPipeConfig);

      // Directly find the initial file from the tree structure
      const initialFile = treeStructure.find(
        tree => tree.name === NEXTFLOW_CONFIG
      );

      // Set all relevant state in a minimized number of updates
      setTreeData(treeStructure);
      setInitialFile(initialFile); // Update comment if assumption about the format was incorrect
      setScriptPipeConfig(editorScriptPipeConfig);
    }
  }, [pipelineDraft]);

  useEffect(() => {
    if (scriptPipeConfig.length > 0) {
      const updated = update([...scriptPipeConfig], file);
      setScriptPipeConfig(updated);
    }
  }, [file]);

  const handleEditorChange = (value: string | undefined): void => {
    increaseChangeCount();
    const modified = { ...file, ...{ text: value } };
    setFile(modified);
  };

  const onTreeChange = (event, node, existing): void => {
    increaseChangeCount();
    switch (event) {
      case AdvancedFilesActions.Delete:
        setFilesToBeDeleted(node);
        break;
      case AdvancedFilesActions.Rename:
        updateFileName(node, existing);
        break;
      case AdvancedFilesActions.Upload: {
        const newConfig = [...scriptPipeConfig];
        node?.data?.forEach(file => {
          newConfig.push({ filename: file?.path, text: file?.text });
        });
        setScriptPipeConfig(newConfig);
        break;
      }
    }
  };

  const updateFileName = (node, existing): void => {
    // Extract `path`, and `text` from `node.data` with fallbacks
    const { path: newPath, text: newText } = node.data || {
      path: "",
      text: "",
    };

    let updatedConfig;

    // Check if the `existing` path is an empty string and the node is a leaf (indicating a new file addition)
    if (existing === "" && node?.isLeaf) {
      // Add the new file to the configuration
      updatedConfig = [
        ...scriptPipeConfig,
        { filename: newPath, text: newText },
      ];
    } else {
      // Update existing file or folder names in the configuration
      updatedConfig =
        scriptPipeConfig.map(item => {
          // Update the filename if it matches `existing`
          if (item.filename === existing) {
            return { filename: newPath, text: item.text };
          }
          // Update children's filenames if updating an entire folder
          else if (item.filename.includes(`${existing}/`)) {
            return {
              filename: item.filename.replace(`${existing}/`, `${newPath}/`),
              text: item.text,
            };
          }
          // Leave the item unchanged if none of the above conditions apply
          return item;
        }) || [];
    }
    // Attempt to find a file in the updated configuration that matches the new path
    const match = updatedConfig.find(item => item.filename === newPath);
    if (match) {
      // If a matching file is found, set it using the provided function
      const lang = getLanguageByFileExtension(match.filename);
      languageRef.current = lang;
      setLanguage(lang);
      setFile(match);
    } else {
      // add this node as a folder
      const newFolder = {
        filename: `${newPath}/`,
        text: "",
      };
      updatedConfig.push(newFolder);
    }
    // Update the main configuration with the new or modified items
    setScriptPipeConfig(updatedConfig);
  };

  const onConfirmDelete = async (node): Promise<boolean> => {
    increaseChangeCount();
    return await new Promise(resolve => {
      const name = node?.data?.name;
      const targetPath = node?.isInternal
        ? `${createPath(node.parent, name)}/`
        : node?.data?.path;
      const filtered = scriptPipeConfig.filter(files =>
        node?.isInternal
          ? !files.filename.startsWith(targetPath)
          : files.filename !== targetPath
      );
      treeRef?.current?.delete(node?.id);
      setScriptPipeConfig(filtered);
      resolve(true);
    });
  };

  const onCancelDelete = (): void => {
    increaseChangeCount();
    setFilesToBeDeleted(null);
  };

  const onSelectFile = (node): void => {
    if (node) {
      const path = node[0]?.data?.path;
      const match = scriptPipeConfig.find(item => {
        return item.filename === path;
      });
      if (match) {
        const lang = getLanguageByFileExtension(match.filename);
        languageRef.current = lang;
        setLanguage(lang);
        setFile(match);
      } else {
        setFile(null);
      }
    }
  };

  const handleLanguageChange = (option): void => {
    increaseChangeCount();
    setLanguage(option.value);
    languageRef.current = option.value;
  };

  return (
    <>
      <div>
        <div className="flex flex-row overflow-hidden">
          <PanelGroup direction="horizontal">
            <Panel
              ref={panelRef}
              collapsible={true}
              onCollapse={() => setPanelCollapsed(true)}
              onExpand={() => setPanelCollapsed(false)}
              minSize={25}
              defaultSize={25}
              maxSize={100}>
              {!!filesToBeDeleted && (
                <DeleteFilesModal
                  files={filesToBeDeleted}
                  setFiles={() => {
                    setFilesToBeDeleted(null);
                  }}
                  onConfirmDelete={onConfirmDelete}
                  onCancelDelete={onCancelDelete}
                />
              )}
              {treeData.length > 0 && (
                <TreeStructure
                  ref={treeRef}
                  treeData={treeData}
                  selection={initialFile?.id}
                  onSelect={onSelectFile}
                  onTreeChange={onTreeChange}
                  showMenu={pipelineDraft?.isEditingAllowed}
                  showSearch={pipelineDraft?.isEditingAllowed}
                />
              )}
              {!HIDE_LANGUAGE_MODAL.includes(file?.filename) && (
                <div
                  className={classNames(
                    "fixed bottom-4 w-[150px]",
                    isPanelCollapsed ? "hidden" : ""
                  )}>
                  <SelectComponent
                    name="scriptMode"
                    menuPlacement="top"
                    enableClear={false}
                    readOnly={!pipelineDraft?.isEditingAllowed}
                    onChange={handleLanguageChange}
                    value={defaultLanguage}
                    options={LANGUAGE_OPTIONS}
                  />
                </div>
              )}
            </Panel>
            <PanelResizer
              panelRef={panelRef}
              isPanelCollapsed={isPanelCollapsed}
            />
            <Panel minSize={30}>
              {file ? (
                <MonacoEditor
                  language={language}
                  defaultValue={file?.text}
                  path={file?.filename}
                  onChange={handleEditorChange}
                  onMount={handleMonacoMount}
                  options={{
                    readOnly:
                      !pipelineDraft?.isEditingAllowed ||
                      file?.text === CANNOT_DISPLAY_FILE,
                    autoClosingBrackets: "never",
                    autoClosingQuotes: "never",
                    formatOnType: true,
                    formatOnPaste: true,
                    trimAutoWhitespace: true,
                    selectionHighlight: true,
                    minimap: {
                      enabled: false,
                    },
                  }}
                  saveViewState={true}
                  editorRef={editorRef}
                />
              ) : (
                <div className="via-generic-error">
                  <EmptyDataIcon />
                  No file selected or available to display.
                </div>
              )}
            </Panel>
          </PanelGroup>
        </div>
      </div>
    </>
  );
}
